/**
 * Memberships hooks using React Relay
 *
 * These hooks provide access to membership data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useMembershipsRelayQuery as UseMembershipsRelayQuery,
  useMembershipsRelayQuery$variables,
  MembershipFilterInput,
  MembershipSortInput,
} from './__generated__/useMembershipsRelayQuery.graphql';
import type { useMembershipsRelayDirectQuery as UseMembershipsRelayDirectQuery } from './__generated__/useMembershipsRelayDirectQuery.graphql';
import type { useMembershipsRelayByIdQuery } from './__generated__/useMembershipsRelayByIdQuery.graphql';
import type { useMembershipsRelayPaginationFragment$key } from './__generated__/useMembershipsRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for memberships list
 */
const MembershipsListPaginationFragment = graphql`
  fragment useMembershipsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useMembershipsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "MembershipFilterInput" }
    sort: { type: "[MembershipSortInput!]" }
  ) {
    listMemberships(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useMembershipsRelayPaginationFragment_listMemberships"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          createdOn
          activeOn
          archivedOn
          authType
          authExpiration
          password
          validEmails
          hideValidEmails
          creditCardHold
          minimumWalletDeposit
          monthlyCost
          yearlyCost
          priority
          maxRentals
          hidden
          brandingColor
          logoImageId
          data
          systemId
          systemOldId
          organizationId
          system {
            id
            oldId
            name
            currencyCode
            mobilityType
          }
          replacementMembership {
            id
            oldId
            name
          }
          perks {
            id
            oldId
            createdOn
            activeOn
            archivedOn
            freeTime
            discount
            moneyOff
            waiveBaseRate
            noFlex
            reservation
            systemOldId
            locationId
            system {
              id
              oldId
              name
              currencyCode
              mobilityType
            }
            location {
              id
              oldId
              name
            }
          }
          prices {
            id
            createdOn
            activeOn
            archivedOn
            price
            priority
            autoRenews
            renewalInterval
            termStartsOn
            termEndsOn
            minutes
            unlocks
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for paginated memberships list (uses fragment for Suspense hooks)
 */
const MembershipsListQuery = graphql`
  query useMembershipsRelayQuery(
    $first: Int
    $after: String
    $filter: MembershipFilterInput
    $sort: [MembershipSortInput!]
  ) {
    ...useMembershipsRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

/**
 * Direct query for memberships list (for non-Suspense fetchQuery usage)
 * This query doesn't use fragments so fetchQuery returns the actual data
 */
const MembershipsListDirectQuery = graphql`
  query useMembershipsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: MembershipFilterInput
    $sort: [MembershipSortInput!]
  ) {
    listMemberships(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          createdOn
          activeOn
          archivedOn
          authType
          authExpiration
          password
          validEmails
          hideValidEmails
          creditCardHold
          minimumWalletDeposit
          monthlyCost
          yearlyCost
          priority
          maxRentals
          hidden
          brandingColor
          logoImageId
          data
          systemId
          systemOldId
          organizationId
          system {
            id
            oldId
            name
            currencyCode
            mobilityType
          }
          replacementMembership {
            id
            oldId
            name
          }
          perks {
            id
            oldId
            createdOn
            activeOn
            archivedOn
            freeTime
            discount
            moneyOff
            waiveBaseRate
            noFlex
            reservation
            systemOldId
            locationId
            system {
              id
              oldId
              name
              currencyCode
              mobilityType
            }
            location {
              id
              oldId
              name
            }
          }
          prices {
            id
            createdOn
            activeOn
            archivedOn
            price
            priority
            autoRenews
            renewalInterval
            termStartsOn
            termEndsOn
            minutes
            unlocks
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for single membership by ID
 */
const MembershipByIdQuery = graphql`
  query useMembershipsRelayByIdQuery($id: ID!) {
    getMembership(id: $id) {
      id
      oldId
      name
      createdOn
      activeOn
      archivedOn
      authType
      authExpiration
      password
      validEmails
      hideValidEmails
      creditCardHold
      minimumWalletDeposit
      monthlyCost
      yearlyCost
      priority
      maxRentals
      hidden
      brandingColor
      logoImageId
      data
      systemId
      systemOldId
      organizationId
      system {
        id
        oldId
        name
        currencyCode
        mobilityType
      }
      replacementMembership {
        id
        oldId
        name
      }
      perks {
        id
        oldId
        createdOn
        activeOn
        archivedOn
        freeTime
        discount
        moneyOff
        waiveBaseRate
        noFlex
        reservation
        systemOldId
        locationId
        system {
          id
          oldId
          name
          currencyCode
          mobilityType
        }
        location {
          id
          oldId
          name
        }
      }
      prices {
        id
        createdOn
        activeOn
        archivedOn
        price
        priority
        autoRenews
        renewalInterval
        termStartsOn
        termEndsOn
        minutes
        unlocks
        name
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { MembershipFilterInput, MembershipSortInput };

/**
 * System type from GraphQL (nested in Membership/Perk)
 */
export interface MembershipSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  currencyCode?: string | null;
  mobilityType?: number | null;
}

/**
 * Location type from GraphQL (nested in Perk)
 */
export interface PerkLocationGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

/**
 * Perk type from GraphQL
 */
export interface PerkGraphQL {
  id: string;
  oldId?: string | null;
  createdOn: string;
  activeOn?: string | null;
  archivedOn?: string | null;
  freeTime?: number | null;
  discount?: number | null;
  moneyOff?: number | null;
  waiveBaseRate?: boolean | null;
  noFlex?: boolean | null;
  reservation?: boolean | null;
  systemOldId?: string | null;
  locationId?: string | null;
  system?: MembershipSystemGraphQL | null;
  location?: PerkLocationGraphQL | null;
}

/**
 * MembershipPrice type from GraphQL
 */
export interface MembershipPriceGraphQL {
  id: string;
  createdOn: string;
  activeOn: string;
  archivedOn?: string | null;
  price?: number | null;
  priority: number;
  autoRenews: boolean;
  renewalInterval?: string | null;
  termStartsOn?: string | null;
  termEndsOn?: string | null;
  minutes?: number | null;
  unlocks?: number | null;
  name?: string | null;
}

/**
 * Replacement Membership type from GraphQL
 */
export interface ReplacementMembershipGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

/**
 * Membership type from GraphQL - matches the fragment fields
 */
export interface MembershipGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  createdOn: string;
  activeOn?: string | null;
  archivedOn?: string | null;
  authType?: string | null;
  authExpiration?: string | null;
  password?: string | null;
  validEmails?: readonly string[] | null;
  hideValidEmails?: boolean | null;
  creditCardHold?: number | null;
  minimumWalletDeposit?: number | null;
  monthlyCost?: number | null;
  yearlyCost?: number | null;
  priority: number;
  maxRentals?: number | null;
  hidden?: boolean | null;
  brandingColor: string;
  logoImageId?: number | null;
  // Json scalar: arrives as an already-parsed object in the response
  data?: unknown;
  systemId?: string | null;
  systemOldId?: string | null;
  organizationId?: string | null;
  system?: MembershipSystemGraphQL | null;
  replacementMembership?: ReplacementMembershipGraphQL | null;
  perks: readonly PerkGraphQL[];
  prices: readonly MembershipPriceGraphQL[];
}

/**
 * Simplified filter options for common use cases
 */
export interface MembershipQueryFilter {
  membershipId?: string;
  systemId?: string;
  /** Filter by multiple system IDs (maps to `systemId: { in }`). Used by the
   * organization view's system multi-select. Ignored when empty. */
  systemIds?: string[];
  systemOldId?: string;
  organizationId?: string;
  name?: string;
  authType?: string;
  status?: 'active' | 'archived' | 'future' | 'all';
  /** Plans whose replacement (old -> new) is this plan's legacy integer id,
   * i.e. the plans this one replaced. */
  replacementMembershipOldId?: string;
  /** true: only plans with an archive date (past or scheduled); false: only
   * plans with none. Unlike `status: 'archived'`, a scheduled archive counts. */
  hasArchiveDate?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching memberships list using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 */
export function useMembershipsRelay(
  filter?: MembershipQueryFilter,
  options?: {
    limit?: number;
    sort?: MembershipSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

  const variables: useMembershipsRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
  };

  // Initial query - suspends while loading
  const queryData = useLazyLoadQuery<UseMembershipsRelayQuery>(
    MembershipsListQuery,
    variables
  );

  // Pagination fragment for load more
  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseMembershipsRelayQuery,
    useMembershipsRelayPaginationFragment$key
  >(MembershipsListPaginationFragment, queryData);

  // Extract nodes from edges
  const nodes =
    data.listMemberships?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as MembershipGraphQL[],
    count: data.listMemberships?.count,
    pageInfo: data.listMemberships?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for fetching a single membership by ID using Relay
 *
 * Uses Suspense - wrap with Suspense boundary
 */
export function useMembershipByIdRelay(membershipId: string) {
  const data = useLazyLoadQuery<useMembershipsRelayByIdQuery>(MembershipByIdQuery, {
    id: membershipId,
  });

  return data.getMembership as MembershipGraphQL | null;
}

// ============================================================================
// Non-Suspense Hooks (with loading state)
// ============================================================================

/**
 * Hook that returns a query loader for memberships list
 *
 * This provides explicit loading state without requiring Suspense.
 */
export function useMembershipsRelayLoader(initialFilter?: MembershipQueryFilter) {
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<UseMembershipsRelayQuery>(
    MembershipsListQuery,
    initialFilter
      ? {
          first: 100,
          filter: buildRelayFilter(initialFilter),
          sort: [{ field: 'CREATED_ON', order: 'DESC' }],
        }
      : undefined
  );

  const load = (
    filter?: MembershipQueryFilter,
    options?: { limit?: number; sort?: MembershipSortInput[] }
  ) => {
    const limit = options?.limit ?? 100;
    const sort = options?.sort ?? [
      { field: 'CREATED_ON' as const, order: 'DESC' as const },
    ];

    loadQuery({
      first: limit,
      filter: buildRelayFilter(filter),
      sort,
    });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded memberships query
 */
export function useMembershipsRelayPreloaded(
  queryRef: PreloadedQuery<UseMembershipsRelayQuery>
) {
  const queryData = usePreloadedQuery<UseMembershipsRelayQuery>(
    MembershipsListQuery,
    queryRef
  );

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseMembershipsRelayQuery,
    useMembershipsRelayPaginationFragment$key
  >(MembershipsListPaginationFragment, queryData);

  const nodes =
    data.listMemberships?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as MembershipGraphQL[],
    count: data.listMemberships?.count,
    pageInfo: data.listMemberships?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for memberships data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched, count, hasMore } = useMembershipsRelayQuery({
 *   systemId: 'uuid',
 *   status: 'active',
 * });
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.map(m => m.name)}</div>;
 */
export function useMembershipsRelayQuery(
  filter?: MembershipQueryFilter,
  options?: {
    limit?: number;
    sort?: MembershipSortInput[];
    enabled?: boolean;
    after?: string | null;
  }
) {
  const [data, setData] = useState<MembershipGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;
  const after = options?.after || undefined;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildRelayFilter(filter);
        const variables: Record<string, unknown> = {
          first: limit,
          sort,
        };
        if (after) {
          variables.after = after;
        }
        if (builtFilter) {
          variables.filter = builtFilter;
        }

        const result = await fetchQuery<UseMembershipsRelayDirectQuery>(
          relayEnvironment,
          MembershipsListDirectQuery,
          variables as useMembershipsRelayQuery$variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listMemberships?.edges?.map((edge) => edge?.node).filter(Boolean) ??
            [];
          setData(nodes as MembershipGraphQL[]);
          setCount(result.listMemberships?.count ?? undefined);
          setHasMore(result.listMemberships?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listMemberships?.pageInfo?.endCursor ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch memberships'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, after, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds a Relay/GraphQL MembershipFilterInput from the simplified filter interface
 */
function buildRelayFilter(
  filter?: MembershipQueryFilter
): MembershipFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: MembershipFilterInput = {};

  if (filter.membershipId) {
    relayFilter.id = { eq: filter.membershipId };
  }
  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemIds && filter.systemIds.length > 0) {
    relayFilter.systemId = { in: filter.systemIds };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.name) {
    relayFilter.name = { ilike: `%${filter.name}%` };
  }
  if (filter.authType) {
    relayFilter.authType = { eq: filter.authType };
  }
  if (filter.replacementMembershipOldId) {
    relayFilter.replacementMembershipOldId = { eq: filter.replacementMembershipOldId };
  }
  if (filter.hasArchiveDate !== undefined) {
    relayFilter.archivedOn = { isNil: !filter.hasArchiveDate };
  }

  // Handle status filter via the server-side calculated booleans
  if (filter.status === 'active') {
    relayFilter.isActive = { eq: true };
  } else if (filter.status === 'archived') {
    relayFilter.isArchived = { eq: true };
  } else if (filter.status === 'future') {
    relayFilter.isFuture = { eq: true };
  }
  // 'all' status means no status filter

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

export { buildRelayFilter };
